import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { productService } from '@/services/product';
import { restaurantService, type RestaurantWithProducts } from '@/services/restaurant';
import { useRealtimeStock } from '@/hooks/useRealtimeStock';
import { useAuth } from '@/contexts/AuthContext';
import { useCartStore } from '@/store/cart';
import { checkOrderAvailability, type Restaurant } from '@/lib/operatingHours';
import { getKoreanDate } from '@/lib/dateUtils';
import type { Product, OrderType, OrderTypeConflict } from '@/pages/store/product-detail.types';

interface UseProductDetailProps {
  storeId: string | undefined;
  productId: string | undefined;
  orderType: OrderType;
}

export function useProductDetail({ storeId, productId, orderType }: UseProductDetailProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const cartStore = useCartStore();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [restaurant, setRestaurant] = useState<RestaurantWithProducts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isOutOfStock, setIsOutOfStock] = useState(false);
  const [isOrderAvailable, setIsOrderAvailable] = useState<boolean>(false);
  const [hasActiveDeadlineOfferToday, setHasActiveDeadlineOfferToday] = useState<boolean>(true);
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [pendingOrderType, setPendingOrderType] = useState<OrderTypeConflict | null>(null);
  const [pendingDirectOrder, setPendingDirectOrder] = useState(false);

  // 데이터 로드
  useEffect(() => {
    const fetchProductDetail = async () => {
      if (!storeId || !productId) return;
      
      try {
        setIsLoading(true);
        
        // 상품 정보 조회
        const productResult = await productService.getProductById(productId);
        
        // 매장 정보 조회 (operating hours 포함)
        const storeResult = await restaurantService.getRestaurantById(storeId);
        if (storeResult.success && storeResult.data) {
          setRestaurant(storeResult.data);
        }
        if (productResult) {
          // 해당 주문유형의 활성화된 오퍼 선택
          const targetOffer = productResult.offers?.find(offer => 
            offer.offer_type === orderType && offer.is_active
          ) || productResult.offers?.find(offer => offer.is_active) || productResult.offers?.[0];

          // 한국 시간 기준 오늘 날짜의 재고만 선택
          const todayKST = getKoreanDate();
          const todayStock = productResult.stock?.find((s: any) => s.date === todayKST);

          // 오늘 활성 마감할인 오퍼 존재 여부 계산 (홈 화면 로직과 동일한 기준)
          if (orderType === 'DEADLINE_DISCOUNT') {
            const hasToday = (productResult.offers || []).some((offer: any) => {
              if (offer.offer_type !== 'DEADLINE_DISCOUNT' || offer.is_active !== true) return false;
              // valid_from이 있으면 오늘(KST)과 일자 비교, 없으면 레거시 호환으로 true
              if (offer.valid_from) {
                try {
                  const offerDate = new Date(offer.valid_from).toISOString().split('T')[0];
                  return offerDate === todayKST;
                } catch {
                  return true;
                }
              }
              return true;
            });
            setHasActiveDeadlineOfferToday(hasToday);
          } else {
            setHasActiveDeadlineOfferToday(true);
          }

          // Convert Product from service to local Product interface
          const localProduct: Product = {
            id: productResult.id,
            name: productResult.name,
            description: productResult.description,
            original_price: productResult.original_price,
            discounted_price: targetOffer?.discounted_price || productResult.original_price,
            discount_rate: targetOffer?.discount_rate || 0,
            discount_type: targetOffer?.offer_type || orderType,
            stock_quantity: todayStock?.current_stock || 0,  // 오늘 재고만 사용
            is_available: productResult.is_available,
            image_url: productResult.image_url
          };
          setProduct(localProduct);
        }
      } catch (error) {
        console.error('Failed to fetch product detail:', error);
        showToast('상품 정보를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductDetail();
  }, [storeId, productId, orderType, showToast]);

  // 실시간 주문 가능 여부 계산
  useEffect(() => {
    if (restaurant && product) {
      try {
        // 기본 주문 가능 여부 체크
        let orderAvailability = checkOrderAvailability(
          restaurant as Restaurant, 
          product.discount_type
        );
        
        // 마감할인의 경우 오늘 활성 오퍼가 있는지도 확인
        if (product.discount_type === 'DEADLINE_DISCOUNT') {
          // has_active_offer_today 대신, 제품 오퍼 기반의 당일 활성 여부 사용
          orderAvailability = orderAvailability && hasActiveDeadlineOfferToday;
        }
        
        setIsOrderAvailable(orderAvailability);
      } catch (error) {
        console.error('Order availability check failed:', error);
        setIsOrderAvailable(false);
      }
    } else {
      setIsOrderAvailable(false);
    }
  }, [restaurant, product, hasActiveDeadlineOfferToday]);

  // 실시간 재고 업데이트 구독
  useRealtimeStock(
    productId ? [productId] : null,
    {
      onStockUpdate: (_, newStock, isAvailable) => {
        if (typeof isAvailable === 'boolean') {
          setProduct(prev => prev ? { ...prev, stock_quantity: newStock, is_available: isAvailable } : null);
        } else {
          setProduct(prev => prev ? { ...prev, stock_quantity: newStock } : null);
        }
        setIsOutOfStock(newStock === 0 || !isAvailable);
        
        // 현재 수량이 재고보다 많으면 조정
        if (newStock > 0 && quantity > newStock) {
          setQuantity(newStock);
        }
      },
      onOutOfStock: (_, productName) => {
        setIsOutOfStock(true);
        setQuantity(1); // 수량 초기화
        showToast(`${productName}이(가) 품절되었습니다`);
      },
      onBackInStock: (_, productName) => {
        setIsOutOfStock(false);
        showToast(`${productName}이(가) 재입고되었습니다`);
      }
    }
  );

  // 주문 가능성 상태 계산
  const getAvailabilityStatus = () => {
    // 1. 재고 없음 (품절)
    if (isOutOfStock || product?.stock_quantity === 0) {
      return {
        text: '품절',
        className: 'bg-red-100 text-red-800',
        isAvailable: false,
        reason: 'stock'
      };
    }

    // 2. 상품 비활성화 (준비중)
    if (!product?.is_available) {
      return {
        text: '준비중',
        className: 'bg-gray-100 text-gray-800',
        isAvailable: false,
        reason: 'product'
      };
    }

    // 3. 주문 시간 마감
    if (!isOrderAvailable) {
      // 예약할인의 경우 예약 전용 메시지
      if (product?.discount_type === 'RESERVATION_DISCOUNT') {
        return {
          text: '예약 마감',
          className: 'bg-gray-100 text-gray-800',
          isAvailable: false,
          reason: 'reservation_closed'
        };
      }
      
      return {
        text: '주문마감',
        className: 'bg-gray-100 text-gray-800',
        isAvailable: false,
        reason: 'time'
      };
    }

    // 4. 모든 조건 만족 (주문가능)
    return {
      text: '주문가능',
      className: 'bg-blue-100 text-blue-800',
      isAvailable: true,
      reason: 'available'
    };
  };

  const availabilityStatus = getAvailabilityStatus();

  // 수량 변경
  const handleQuantityChange = (newQuantity: number) => {
    if (!product) return;
    
    if (newQuantity < 1) {
      setQuantity(1);
    } else if (newQuantity > product.stock_quantity) {
      setQuantity(product.stock_quantity);
      showToast(`재고가 ${product.stock_quantity}개만 남았습니다`);
    } else {
      setQuantity(newQuantity);
    }
  };

  // 장바구니에 담기
  const handleAddToCart = async () => {
    if (!product || !storeId) return;
    
    // 주문 불가능 상태 확인
    if (!availabilityStatus.isAvailable) {
      switch (availabilityStatus.reason) {
        case 'stock':
          showToast('품절된 상품입니다');
          break;
        case 'product':
          showToast('현재 준비 중인 상품입니다');
          break;
        case 'time':
          showToast('주문 시간이 마감되었습니다');
          break;
        case 'reservation_closed':
          showToast('예약 주문 시간이 마감되었습니다');
          break;
        default:
          showToast('주문할 수 없는 상품입니다');
      }
      return;
    }
    
    // 로그인 상태 확인
    if (!user) {
      showToast('로그인이 필요합니다');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    if (product.stock_quantity < quantity) {
      showToast('선택한 수량이 재고보다 많습니다');
      return;
    }
    
    try {
      setIsAddingToCart(true);
      
      // 장바구니에 추가
      const result = await cartStore.addItem(
        storeId,
        restaurant?.name || '',
        product.discount_type,
        {
          productId: product.id,
          productName: product.name,
          productImage: product.image_url,
          originalPrice: product.original_price,
          discountedPrice: product.discounted_price,
          discountRate: product.discount_rate,
          quantity: quantity,
          currentStock: product.stock_quantity,
          options: undefined
        },
        user?.id
      );
      
      if (result.success) {
        showToast(result.message || '장바구니에 추가되었습니다');
        
        // 장바구니 페이지로 이동할지 물어보기
        setTimeout(() => {
          const goToCart = window.confirm('장바구니로 이동하시겠습니까?');
          if (goToCart) {
            navigate('/cart');
          }
        }, 1000);
      } else {
        // 주문유형 충돌 확인
        if (result.message === 'DIFFERENT_ORDER_TYPE' && result.data) {
          setPendingOrderType({
            currentType: result.data.currentOrderType,
            newType: result.data.newOrderType
          });
          setShowOrderTypeModal(true);
        } else {
          showToast(result.message || '장바구니 추가에 실패했습니다');
        }
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showToast('장바구니에 추가하는데 실패했습니다');
    } finally {
      setIsAddingToCart(false);
    }
  };

  // 바로 주문하기
  const handleBuyNow = async () => {
    if (!product || !storeId) return;
    
    // 주문 불가능 상태 확인
    if (!availabilityStatus.isAvailable) {
      switch (availabilityStatus.reason) {
        case 'stock':
          showToast('품절된 상품입니다');
          break;
        case 'product':
          showToast('현재 준비 중인 상품입니다');
          break;
        case 'time':
          showToast('주문 시간이 마감되었습니다');
          break;
        case 'reservation_closed':
          showToast('예약 주문 시간이 마감되었습니다');
          break;
        default:
          showToast('주문할 수 없는 상품입니다');
      }
      return;
    }
    
    // 로그인 상태 확인
    if (!user) {
      showToast('로그인이 필요합니다');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    if (product.stock_quantity < quantity) {
      showToast('선택한 수량이 재고보다 많습니다');
      return;
    }
    
    // 장바구니에 추가하고 장바구니 페이지로 이동
    const result = await cartStore.addItem(
      storeId,
      restaurant?.name || '',
      product.discount_type,
      {
        productId: product.id,
        productName: product.name,
        productImage: product.image_url,
        originalPrice: product.original_price,
        discountedPrice: product.discounted_price,
        discountRate: product.discount_rate,
        quantity: quantity,
        currentStock: product.stock_quantity,
        options: undefined
      },
      user?.id
    );
    
    if (result.success) {
      // 성공 시 토스트 메시지 표시 후 적절한 시간 뒤 장바구니 페이지로 이동
      showToast('장바구니에 추가되어 주문 페이지로 이동합니다');
      setTimeout(() => {
        navigate('/cart');
      }, 1000); // 1초 후 이동하여 사용자가 토스트를 읽을 시간 제공
    } else {
      // 주문유형 충돌 확인
      if (result.message === 'DIFFERENT_ORDER_TYPE' && result.data) {
        setPendingOrderType({
          currentType: result.data.currentOrderType,
          newType: result.data.newOrderType
        });
        setPendingDirectOrder(true); // 바로 주문 모드 표시
        setShowOrderTypeModal(true);
      } 
      // 다른 가게 상품 충돌 처리 (장바구니가 다른 가게 상품으로 차있을 때)
      else if (result.message?.includes('이전 장바구니가 초기화')) {
        // 이미 처리됨 - 성공 메시지와 함께 장바구니로 이동
        showToast(result.message);
        navigate('/cart');
      }
      else {
        showToast(result.message || '장바구니 추가에 실패했습니다');
      }
    }
  };

  // 주문 유형 모달 핸들러
  const handleOrderTypeConfirm = async () => {
    if (!product || !pendingOrderType || !storeId) return;
    
    // 기존 장바구니를 비우고 새 상품 추가
    await cartStore.clearCart(user?.id);
    const result = await cartStore.addItem(
      storeId,
      restaurant?.name || '',
      product.discount_type,
      {
        productId: product.id,
        productName: product.name,
        productImage: product.image_url,
        originalPrice: product.original_price,
        discountedPrice: product.discounted_price,
        discountRate: product.discount_rate,
        quantity: quantity,
        currentStock: product.stock_quantity,
        options: undefined
      },
      user?.id
    );
    
    if (result.success) {
      showToast('장바구니에 추가되었습니다');
      
      // 바로 주문 모드였다면 장바구니로 이동
      if (pendingDirectOrder) {
        setTimeout(() => {
          navigate('/cart');
        }, 1000);
      } else {
        // 일반 장바구니 담기였다면 이동 여부 확인
        setTimeout(() => {
          const goToCart = window.confirm('장바구니로 이동하시겠습니까?');
          if (goToCart) {
            navigate('/cart');
          }
        }, 1000);
      }
    } else {
      showToast(result.message || '장바구니 추가에 실패했습니다');
    }
    
    // 모달 닫기
    setShowOrderTypeModal(false);
    setPendingOrderType(null);
    setPendingDirectOrder(false);
  };

  const handleOrderTypeCancel = () => {
    setShowOrderTypeModal(false);
    setPendingOrderType(null);
    setPendingDirectOrder(false);
  };

  return {
    // State
    product,
    restaurant,
    isLoading,
    quantity,
    isAddingToCart,
    isOutOfStock,
    isOrderAvailable,
    showOrderTypeModal,
    pendingOrderType,
    pendingDirectOrder,
    availabilityStatus,
    
    // Actions
    setQuantity: handleQuantityChange,
    handleAddToCart,
    handleBuyNow,
    handleOrderTypeConfirm,
    handleOrderTypeCancel
  };
}
